import React from 'react';
import './Legal.css';

function InstructorTerms() {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <h1>Instructor Terms & Referral Agreement</h1>
        <p className="last-updated">Last Updated: January 29, 2026</p>
      </div>

      <div className="legal-content">
        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            This Instructor Referral Agreement ("Agreement") governs the relationship between 
            Fast Track Driving School ("FTDS", "we", "us") and independent Approved Driving 
            Instructors ("ADI", "Instructor", "you") who register on our platform.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Nature of Relationship</h2>
          <p>
            <strong>Important:</strong> This Agreement establishes an independent contractor 
            relationship. You are NOT an employee, partner, or agent of Fast Track Driving School.
          </p>
          <ul>
            <li>You operate as a self-employed independent instructor</li>
            <li>You are responsible for your own taxes, insurance, and business operations</li>
            <li>You maintain full control over your schedule and teaching methods</li>
            <li>We provide student referrals; you provide driving instruction</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Student Referral &amp; Payment Model</h2>
          <p>3.1 The Company operates as a student referral and marketing platform connecting Students with independent Approved Driving Instructors.</p>
          <p>3.2 The Company collects an upfront booking fee from the Student. This upfront fee varies depending on the lesson package selected by the Student (Trial, Bronze, Silver, or Gold).</p>
          <p>3.3 This upfront booking fee represents a referral and marketing fee payable to Fast Track Driving School for providing verified students to the Instructor.</p>
          <p>3.4 <strong>The Instructor must not request or accept any payment from the Student that corresponds to the upfront fee already paid to the Company.</strong></p>
          <p>3.5 After the first lesson is completed, the Instructor may continue providing lessons and collect the remaining lesson payments directly from the Student.</p>
          <p>3.6 The standard lesson rate used by the Company is £37 per hour.</p>
          <p>3.7 The Company has no obligation to provide a minimum number of Students or referrals.</p>
        </section>

        <section className="legal-section">
          <h2>4. How Referrals Work</h2>
          <ol>
            <li>Students book discounted introductory lessons through our website</li>
            <li>We match students with suitable instructors based on location and preferences</li>
            <li>We provide you with student contact details and booking information</li>
            <li>You contact the student to arrange the introductory lesson</li>
            <li>After the introductory lesson, ongoing instruction is arranged directly between 
            you and the student</li>
          </ol>
        </section>

        <section className="legal-section">
          <h2>5. Referral &amp; Marketing Fee</h2>
          <p>5.1 The upfront booking fee paid by the Student represents a non-refundable referral and marketing fee payable to the Company.</p>
          <p>5.2 The amount of the referral and marketing fee varies depending on the lesson package selected by the Student.</p>
          <p>5.3 The Company retains 100% of the upfront booking fee.</p>
          <p>5.4 The Instructor shall collect the remaining lesson payments directly from the Student after completion of the first lesson.</p>
          <p>5.5 All fees are exclusive of VAT unless otherwise stated.</p>
        </section>

        <section className="legal-section">
          <h2>Important Obligations</h2>
          <p>The Instructor agrees to:</p>
          <ul>
            <li>Not cancel the first lesson after approving a Student booking</li>
            <li>Maintain valid ADI licence, DBS, insurance, and MOT</li>
            <li>Notify the Company immediately when any document expires</li>
            <li>Deliver instruction professionally and safely</li>
            <li>Not bypass or circumvent the Company's referral system</li>
            <li>Comply with UK laws and DVSA regulations</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Instructor Obligations</h2>
          <p>As an independent instructor, you agree to:</p>
          <ul>
            <li>Respond to student referrals within 24 hours</li>
            <li>Conduct introductory lessons professionally and safely</li>
            <li>Maintain valid insurance and certifications at all times</li>
            <li>Notify us immediately of any changes to your ADI status, insurance, or availability</li>
            <li>Treat all students with respect and professionalism</li>
            <li>Comply with DVSA standards of instruction</li>
            <li>Not discriminate based on protected characteristics</li>
            <li>Keep accurate records of lessons and student progress</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Insurance and Liability</h2>
          <p>
            <strong>You are solely responsible for:</strong>
          </p>
          <ul>
            <li>Maintaining comprehensive driving instructor insurance</li>
            <li>Ensuring your vehicle is properly insured for tuition purposes</li>
            <li>Any incidents, accidents, or damages during lessons</li>
            <li>Student safety during instruction</li>
            <li>Compliance with all traffic laws</li>
          </ul>
          <p>
            Fast Track Driving School accepts no liability for incidents occurring during lessons. 
            You must have adequate professional indemnity and public liability insurance.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Prohibited Activities</h2>
          <p>You must NOT:</p>
          <ul>
            <li>Use our platform if your ADI licence is suspended or revoked</li>
            <li>Misrepresent your qualifications or experience</li>
            <li>Conduct yourself in an unprofessional manner</li>
            <li>Discriminate against students</li>
            <li>Share student information inappropriately</li>
            <li>Circumvent our platform to avoid referral fees</li>
            <li>Engage in any fraudulent or illegal activities</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9. Termination</h2>
          <p>Either party may terminate this Agreement:</p>
          <ul>
            <li>With 14 days' written notice for any reason</li>
            <li>Immediately if the other party breaches material terms</li>
            <li>Immediately if your ADI licence is suspended or revoked</li>
            <li>Immediately if you fail to maintain required insurance</li>
          </ul>
          <p>
            Upon termination, you must cease using our platform and branding. Outstanding fees remain 
            due and payable.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Data Protection</h2>
          <p>
            You agree to handle student personal data in accordance with UK data protection laws. 
            You may only use student information for the purpose of providing driving instruction.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Intellectual Property</h2>
          <p>
            You may not use our name, logo, or branding without prior written consent. You retain 
            ownership of your own teaching materials and methods.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Updates to Agreement</h2>
          <p>
            We reserve the right to modify this Agreement. Material changes will be communicated 
            via email. Continued use of our platform constitutes acceptance of modified terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Governing Law</h2>
          <p>
            This Agreement is governed by the laws of England and Wales. Disputes will be subject 
            to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Contact</h2>
          <p>
            For questions about this Agreement, please contact us through our Contact page.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Acknowledgment</h2>
          <p>
            By registering as an instructor on our platform, you acknowledge that you have read, 
            understood, and agree to be bound by this Instructor Referral Agreement.
          </p>
        </section>
      </div>
    </div>
  );
}

export default InstructorTerms;
